import config from '../config';
import handler from './handler';
import axios from 'axios';
import { localStorageWrapper } from '../../helpers/storageFactory';
import { ACCOUNT, LOGIN_SUCCESS } from '../../redux/actions/accountActions';
import store from '../../redux/store';
import { WIDGET_LOAD } from '../../redux/actions/widgetActions';
const CancelToken = axios.CancelToken;

const defaults = {
    debug: true,
    clear_api_host: config.api_host,
    headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
    },
};

const getConfigData = (withCredentials, data, formData) => {
    const configData = {
        headers: { ...defaults.headers },
        withCredentials: !!withCredentials,
    };

    if (data) {
        // GET
        configData.headers['Content-Type'] = 'text/plain';
        configData.params = data;
    }
    if (formData) {
        configData.headers['Content-Type'] = 'multipart/form-data';
    }

    const accessToken = localStorageWrapper.getItem('TapAccountInteractionSite');

    if (accessToken) {
        configData.headers.Authorization = `Bearer ${JSON.parse(accessToken)}`;
    }
    return configData;
};

const getAccessToken = () => {
    const token = localStorageWrapper.getItem('TapAccountInteractionSite');
    return token ? JSON.parse(token) : null;
};

const refreshToken = async () => {
    const token = getAccessToken();

    if (token) {
        const response = await axios.post(
            defaults.clear_api_host + '/interactionSite/auth/refreshTokens',
            { token },
            getConfigData(true)
        );

        if (response.data && response.data.data && response.data.data.token) {
            const newToken = response.data.data.token;
            localStorageWrapper.setItem('TapAccountInteractionSite', JSON.stringify(newToken));
            return newToken;
        }
    }

    return null;
};

axios.interceptors.request.use(
    (config) => {
        const accessToken = getAccessToken();
        if (accessToken) {
            config.headers.Authorization = `Bearer ${accessToken}`;
            // Set the Authorization header on the axios instance instead
        }
        return config;
    },
    (error) => Promise.reject(error)
);

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach((promise) => {
        if (error) {
            promise.reject(error);
        } else {
            promise.resolve(token);
        }
    });

    failedQueue = [];
};

axios.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;
        if (error.response && error.response.status === 401 && !originalRequest._retry) {
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                })
                    .then((token) => {
                        originalRequest.headers.Authorization = `Bearer ${token}`;
                        return axios(originalRequest);
                    })
                    .catch((error_) => {
                        return Promise.reject(error_);
                    });
            }
            originalRequest._retry = true;
            isRefreshing = true;

            return refreshToken()
                .then((newAccessToken) => {
                    if (newAccessToken) {
                        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
                        processQueue(null, newAccessToken);
                        store.dispatch({
                            type: LOGIN_SUCCESS,
                            payload: { token: newAccessToken },
                        });
                        return axios(originalRequest);
                    }
                    handler.authError();
                    return Promise.reject(error);
                })
                .catch((error_) => {
                    console.log(error_);
                    handler.authError();
                    processQueue(error_, null);
                    return Promise.reject(error_);
                })
                .finally(() => {
                    isRefreshing = false;
                });
        }

        return Promise.reject(error);
    }
);

const getOnThen = (response, handleError) => {
    if (!response.data.status && response.data.error) {
        if (handleError) {
            handler.errorHandler(response);
        }
        return {
            success: false,
        };
    } else {
        return response.data;
    }
};

const getOnCatch = (err, handleError) => {
    console.log(err);
    if (handleError) {
        if (!axios.isCancel(err)) {
            handler.errorHandler(err);
            return err.response && err.response.data
                ? {
                      errorData: err.response.data,
                      success: false,
                  }
                : {
                      success: false,
                  };
        }
        return {
            success: false,
        };
    } else {
        if (err.response.status === 503) {
            store.dispatch({
                type: ACCOUNT.SET_MAINTENANCE_MODE,
            });
            store.dispatch({
                type: WIDGET_LOAD,
            });
        }
        return {
            cancelled: !!axios.isCancel(err),
            success: false,
        };
    }
};

const getTypePrefix = (type) => {
    let prefix = '/widget';
    switch (type) {
        case 'interaction':
            prefix = '/interactionSite';
            break;
        case 'common':
            prefix = '/common';
            break;
        case 'public':
            prefix = '/public';
            break;
    }
    return prefix;
};

class HttpServices {
    static async get({ path, data, currentToken = false, withCredentials = false, typeError, requestType = 'widget' }) {
        if (typeError) {
            handler.typeError(typeError);
            return {
                success: false,
            };
        }
        if (currentToken && window.tapCancelApiCall != undefined) {
            window.tapCancelApiCall();
            window.tapCancelApiCall = undefined;
        }

        const configData = getConfigData(withCredentials, data);
        if (currentToken) {
            configData.cancelToken = new CancelToken((c) => {
                window.tapCancelApiCall = c;
            });
        }

        const prefix = getTypePrefix(requestType);
        const handleError = requestType === 'interaction' || requestType === 'common';

        return axios
            .get(defaults.clear_api_host + prefix + path, configData)
            .then((response) => getOnThen(response, handleError))
            .catch((error) => getOnCatch(error, handleError));
    }

    static async post({ path, data = {}, withCredentials = false, typeError, requestType = 'widget' }) {
        if (typeError) {
            handler.typeError(typeError);
            return {
                success: false,
            };
        }
        const prefix = getTypePrefix(requestType);
        const handleError = requestType === 'interaction' || requestType === 'common';

        return axios
            .post(defaults.clear_api_host + prefix + path, data, getConfigData(withCredentials))
            .then((response) => getOnThen(response, handleError))
            .catch((error) => getOnCatch(error, handleError));
    }

    static async put({ path, data = {}, withCredentials = false, requestType = 'widget' }) {
        const prefix = getTypePrefix(requestType);
        const handleError = requestType === 'interaction' || requestType === 'common';

        return axios
            .put(defaults.clear_api_host + prefix + path, data, getConfigData(withCredentials))
            .then((response) => getOnThen(response, handleError))
            .catch((error) => getOnCatch(error, handleError));
    }
}

export default HttpServices;
